'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { redirect } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { usd1PointsBunja } from '@flipster/features/src/usd1-points/bunja';
import { TextButton } from '@flipster/react/design-system';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { Usd1PointsEvent } from '@flipster/utils/segment/events/usd1-points';

import WalletModal from '../_modal/WalletModal';
import WlfiAndFlipsterIcon from './_assets/wlfi-and-flipster.svg';
import HoldSection from './_components/hold-section';
import TotalPoints from './_components/total-points';
import TradeSection from './_components/trade-section';

const learnMoreUrl = 'https://flipster.io/en/campaign/flipster-wlfi';

const Page = () => {
  const { usd1PointsAtom } = useBunja(usd1PointsBunja);
  const usd1Points = useAtomValue(usd1PointsAtom);
  const { getVariantAtom, fetchedAtom } = useBunja(abTestBunja);
  const { variantAtom } = getVariantAtom('25q3-ti-usd1-points-dashboard');
  const isAbTestFetched = useAtomValue(fetchedAtom);
  const isEnabledUsd1PointsDashboard = useAtomValue(variantAtom) === 'treatment';
  const { isCookieLoggedIn } = useAuth();
  const mounted = useMounted();
  const isZeroPoint = usd1Points?.totalPoints === 0 || !isCookieLoggedIn;
  const isValidAbTestRef = useRef(false);

  useEffect(() => {
    if (isValidAbTestRef.current) return;

    if (isAbTestFetched) {
      isValidAbTestRef.current = true;
      if (!isEnabledUsd1PointsDashboard) redirect('/404');
    }
  }, [isAbTestFetched, isEnabledUsd1PointsDashboard]);

  if (!mounted || !isAbTestFetched) return null;
  return (
    <Suspense>
      <main
        className={css({
          maxW: 'desktopMaxWidth',
          mx: 'auto',
          color: 'content.default.level-4',
          pt: 80,
          pb: 420,
          px: { smallDesktopDown: 16 },
        })}
      >
        <div className={vstack({ gap: 8, alignItems: 'flex-start' })}>
          <div className={hstack({ gap: 16, textStyle: 'Title/XS', alignItems: 'center' })}>
            <div className={hstack({ gap: 4 })}>
              <WlfiAndFlipsterIcon />
            </div>
          </div>
          <h1 className={css({ textStyle: 'Display/XL' })}>USD1 points program</h1>
          <TextButton
            as='a'
            href={learnMoreUrl}
            target='_blank'
            label='Learn more'
            size='large'
            onClick={() => {
              segment.track(Usd1PointsEvent.Usd1PageLearnMoreClick);
            }}
          />
        </div>

        {isCookieLoggedIn && (
          <div className={flex({ justifyContent: 'center', mt: 64 })}>
            <TotalPoints />
          </div>
        )}

        <div className={vstack({ gap: 4, alignItems: 'flex-start', mt: 64 })}>
          {isZeroPoint ? (
            <h2 className={css({ textStyle: 'Display/L' })}>Trade or hold to start earning points</h2>
          ) : (
            <>
              <h2 className={css({ textStyle: 'Display/L' })}>Earn more points</h2>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                Trade or hold to earn USD1 loyalty points.
              </p>
            </>
          )}
        </div>
        <HoldSection />
        <TradeSection />
      </main>
      <WalletModal />
    </Suspense>
  );
};

export default Page;
